import { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ReorderActivityButtonsModal from '../modal/ReorderActivityButtonsModal';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'cust... Remove this comment to see the full error message

import Raven from 'raven-js';
import ErrorBoundary from 'crm-activity-creator-widgets/buttons/ErrorBoundary';
import { BUTTON_COMPONENTS } from '../constants/ButtonComponents';
import LabelledIconButton from './LabelledIconButton';
import ErrorButton from './ErrorButton';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import I18n from 'I18n';
import { usePopoverClickListener } from '../hooks/usePopoverClickListener';
import { ACTIVITY_CREATOR_INTERACTIONS, CRM_ERROR_BOUNDARY } from 'customer-data-tracking/constants/eventNames';
import { DEFAULT_MAX_COUNT } from '../constants/ButtonConstants';
import { useCreateActivityPermissions } from 'crm-activity-creator-data/permission/CreateActivityPermissionsContext';
import { Metrics } from 'crm-activity-creator-data/utils/Metrics';
import { BUTTON_ERRORS } from 'crm-activity-creator-data/constants/errors';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import CreateActivityPopover from '../popover/CreateActivityPopover';
import { HR } from 'foundations-components/transitional/components/HR';
import { Spacer } from 'foundations-components/components/Spacer';
import { EllipsesIcon } from 'foundations-assets/icons/components/EllipsesIcon';

/* @bend-expect trellis-tokenized */
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ZeroLineHeight = styled.span.withConfig({
  displayName: "OverflowActivityButton__ZeroLineHeight",
  componentId: "dqeg4p-0"
})(["line-height:0;"]);

// This mirrors the styles of Select-no-results from the UI library because it is not an exposed component.
// The only change is there is no padding bottom in this version as per the UI mocks.
/* @bend-expect trellis-tokenized */
const StyledSelectNoResults = styled.div.withConfig({
  displayName: "OverflowActivityButton__StyledSelectNoResults",
  componentId: "dqeg4p-1"
})(["background-color:", ";border-color:", ";color:", ";cursor:not-allowed;padding-block-start:", ";padding-block-end:", ";padding-inline:", ";"], ({
  theme
}) => theme.color['fill-surface-default'], ({
  theme
}) => theme.color['border-tertiary-default'], ({
  theme
}) => theme.color['text-primary-disabled'], ({
  theme
}) => theme.space['200'], ({
  theme
}) => theme.space['0'], ({
  theme
}) => theme.space['500']);
function OverflowActivityButton({
  allActivityButtons,
  objectId,
  objectTypeId,
  taskId,
  buttonSize,
  isError,
  maxVisible = DEFAULT_MAX_COUNT,
  enableReorder,
  hideReorderButton = false,
  useCallingWidget = false,
  onCloseModal,
  onOpenModal,
  onUpdate,
  OverrideButtonComponent
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(undefined);
  const {
    currentUserCanCommunicate
  } = useCreateActivityPermissions();
  const popoverContentRef = useRef(null);
  const handleOverflowClose = () => {
    setIsOverflowOpen(false);
  };
  const contentRef = useRef(null);
  usePopoverClickListener({
    contentRef,
    onClose: handleOverflowClose
  });
  const selectedActivityButtons = allActivityButtons.slice(0, maxVisible);
  const overflowActivityButtons = useMemo(() => {
    const selectedActvityButtonsSet = new Set(selectedActivityButtons);
    return allActivityButtons.filter(interactionName => !selectedActvityButtonsSet.has(interactionName));
  }, [allActivityButtons, selectedActivityButtons]);
  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
    CrmLogger.log(ACTIVITY_CREATOR_INTERACTIONS, {
      action: 'Open reorder activity modal'
    });
    setIsOverflowOpen(false);
  }, [setIsModalOpen]);
  const handleCancelOrClose = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);
  const handleSave = useCallback(newSelectedActivityButtons => {
    onUpdate(newSelectedActivityButtons);
    setIsModalOpen(false);
    if (isError) {
      FloatingAlertStore.addAlert({
        titleText: I18n.text('reorderActivityButtonsToast.errorTitle'),
        message: I18n.text('reorderActivityButtonsToast.errorSave'),
        type: 'danger'
      });
      return;
    }
    FloatingAlertStore.addAlert({
      titleText: I18n.text('reorderActivityButtonsToast.successTitle'),
      message: I18n.text('reorderActivityButtonsToast.successfulSave'),
      type: 'success'
    });
  }, [setIsModalOpen, onUpdate, isError]);
  const handleDropdownOpen = useCallback(() => {
    CrmLogger.log(ACTIVITY_CREATOR_INTERACTIONS, {
      action: 'Open activity overflow'
    });
  }, []);
  const renderedReorderActivityButtonsLink = useMemo(() => {
    return /*#__PURE__*/_jsx(View, {
      children: /*#__PURE__*/_jsx(Button, {
        "data-selenium-test": "reorder-activity-buttons-option",
        onClick: handleOpenModal,
        use: "transparent",
        disabled: !enableReorder,
        children: /*#__PURE__*/_jsx(ZeroLineHeight, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reorderActivityButtonsModal.title"
          })
        })
      })
    });
  }, [handleOpenModal, enableReorder]);
  const updateSearchInput = useCallback(event => {
    const {
      value
    } = event.target;
    setSearchInput(value);
  }, [setSearchInput]);
  const renderedSearchInput = useMemo(() => {
    return /*#__PURE__*/_jsx(View, {
      backgroundColor: "fill-surface-recessed",
      paddingBlock: "300",
      paddingInline: "300",
      children: /*#__PURE__*/_jsx(SearchInput, {
        "aria-label": I18n.text('createActivityButtons.overflowMenu.header'),
        "data-test-id": "overflow-search",
        onChange: updateSearchInput,
        use: "on-dark",
        value: searchInput
      })
    });
  }, [searchInput, updateSearchInput]);

  // Render each overflow activity button which appears in the filter
  const renderedFilteredButtons = useCallback(filteredOverflowButtons => filteredOverflowButtons.map(interactionName => {
    if (!interactionName) {
      return null;
    }
    const ButtonComponent = BUTTON_COMPONENTS[interactionName];
    const handleOnButtonDidCatch = error => {
      Metrics.counter(BUTTON_ERRORS, {
        interaction_type: interactionName
      }).increment();
      Raven.captureMessage('Error encountered in activity creator overflow option', {
        fingerprint: ['ERROR_ACTIVITY_CREATOR_OVERFLOW_OPTION'],
        extra: {
          objectTypeId,
          subjectId: objectId,
          interactionName
        }
      });
      CrmLogger.log(CRM_ERROR_BOUNDARY, {
        action: error && (error.message || 'Unknown error'),
        type: 'activity-creator-overflow-option-boundary'
      });
    };
    const handleButtonComponentClick = overflowToggleAction => {
      const {
        targetState
      } = overflowToggleAction || {};
      const nextOpenState = targetState !== undefined ? targetState : false;
      setIsOverflowOpen(nextOpenState);
    };
    const renderedErroredButton = () => /*#__PURE__*/_jsx(ErrorButton, {
      buttonSize: buttonSize,
      interactionName: interactionName || '',
      isOverflow: true,
      objectId: objectId,
      objectTypeId: objectTypeId
    });
    return /*#__PURE__*/_jsx(ErrorBoundary, {
      boundaryName: `activity-button-boundary-${interactionName}`,
      onComponentDidCatch: handleOnButtonDidCatch,
      ErrorComponent: renderedErroredButton,
      children: /*#__PURE__*/_jsx(ButtonComponent, {
        interactionName: interactionName,
        objectId: objectId,
        objectTypeId: objectTypeId,
        taskId: taskId,
        buttonSize: buttonSize,
        currentUserCanCommunicate: currentUserCanCommunicate,
        isOverflow: true,
        useCallingWidget: useCallingWidget,
        onClickActionComplete: handleButtonComponentClick,
        onCloseModal: onCloseModal,
        onOpenModal: onOpenModal
      })
    }, interactionName);
  }), [buttonSize, currentUserCanCommunicate, objectId, objectTypeId, onCloseModal, onOpenModal, taskId, useCallingWidget]);
  const renderedOverflowActivityOptions = useMemo(() => {
    // Filter based on search input, don't filter if there is no input.
    const filteredOverflowButtons = overflowActivityButtons.filter(overflowActivityButton => {
      // Based on lang file to support the full string and multiple languages.
      const activityButtonText = I18n.text(`createActivityButtons.action.${overflowActivityButton}`).toLowerCase();
      return !searchInput || activityButtonText.toLowerCase().includes(searchInput);
    });
    if (filteredOverflowButtons.length > 0) {
      return renderedFilteredButtons(filteredOverflowButtons);
    }
    return (
      /*#__PURE__*/
      /* @bend-expect trellis-tokenized */
      _jsx(StyledSelectNoResults, {
        "data-test-id": "overflow-no-results",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "createActivityButtons.overflowMenu.noResults"
        })
      })
    );
  }, [overflowActivityButtons, renderedFilteredButtons, searchInput]);
  const renderedOverflowActivityInteractions = useMemo(() => {
    return /*#__PURE__*/_jsx(View, {
      id: "overflow-activities-list",
      as: "div",
      maxBlockSize: "222px",
      overflowY: "scroll",
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        children: renderedOverflowActivityOptions
      })
    });
  }, [renderedOverflowActivityOptions]);
  const renderedPopoverContent = useMemo(() => {
    if (!overflowActivityButtons.length) {
      return renderedReorderActivityButtonsLink;
    }
    return /*#__PURE__*/_jsx(View, {
      "data-test-id": "overflow-selectors-menu-content",
      role: "menu",
      ref: popoverContentRef,
      tabIndex: -1,
      overflow: "hidden",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [renderedSearchInput, renderedOverflowActivityInteractions, hideReorderButton ? /*#__PURE__*/_jsx(Spacer, {
          space: "200"
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(HR, {
            marginBlock: "200"
          }), /*#__PURE__*/_jsx(View, {
            marginBlockEnd: "200",
            children: renderedReorderActivityButtonsLink
          })]
        })]
      })
    });
  }, [overflowActivityButtons.length, renderedSearchInput, renderedOverflowActivityInteractions, hideReorderButton, renderedReorderActivityButtonsLink]);
  const handleButtonClick = useCallback(() => {
    setIsOverflowOpen(!isOverflowOpen);
    if (!isOverflowOpen) {
      handleDropdownOpen();
      if (popoverContentRef.current) {
        popoverContentRef.current.focus();
      }
    }
  }, [isOverflowOpen, handleDropdownOpen]);
  const handlePopoverToggle = targetState => {
    setIsOverflowOpen(targetState);
  };
  const hasOnlyOverflowActivities = overflowActivityButtons.length === allActivityButtons.length;
  const overflowTooltipKey = hasOnlyOverflowActivities ? 'createActivityButtons.tooltip.overflowWithNoOptionsSelected' : 'createActivityButtons.tooltip.overflow';
  const renderedOverflowButton = useMemo(() => {
    if (OverrideButtonComponent) {
      return /*#__PURE__*/_jsx(OverrideButtonComponent, {
        ariaLabel: I18n.text('createActivityButtons.action.overflow'),
        buttonSize: buttonSize,
        seleniumSelector: "create-activity-overflow-button",
        dataTestId: "create-activity-overflow-button",
        iconName: "ellipses",
        isPopoverOpen: isOverflowOpen,
        onClick: handleButtonClick
      }, "overflow");
    }
    return /*#__PURE__*/_jsx(LabelledIconButton, {
      ariaLabel: I18n.text('createActivityButtons.action.overflow'),
      buttonSize: buttonSize,
      seleniumSelector: "create-activity-overflow-button",
      dataTestId: "create-activity-overflow-button",
      IconComponent: EllipsesIcon,
      iconName: "ellipses",
      isPopoverOpen: isOverflowOpen,
      label: I18n.text(overflowTooltipKey),
      onClick: handleButtonClick,
      tooltip: I18n.text(overflowTooltipKey)
    }, "overflow");
  }, [buttonSize, isOverflowOpen, overflowTooltipKey, handleButtonClick, OverrideButtonComponent]);
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-test": "activity-button-overflow-dropdown",
    children: [/*#__PURE__*/_jsx(CreateActivityPopover, {
      open: isOverflowOpen,
      onPopoverToggle: handlePopoverToggle,
      content: renderedPopoverContent,
      interactionName: "overflow",
      isOverflow: true,
      children: renderedOverflowButton
    }), isModalOpen && /*#__PURE__*/_jsx(ReorderActivityButtonsModal, {
      allActivityButtons: allActivityButtons,
      onCancel: handleCancelOrClose,
      onSave: handleSave,
      selectedActivityButtons: selectedActivityButtons,
      objectTypeId: objectTypeId
    })]
  });
}
export default OverflowActivityButton;

// for testing purposes
export { OverflowActivityButton as WrappedComponent };